/**
 * Auto-name pipeline for paired devices.
 *
 * Three tiers, evaluated in order:
 *   A. User-chosen signal (UA-CH `model`)
 *   B. Friendly factual default (UA-parsed browser name, capped at 22 chars)
 *   C. Humble platform default ("Browser")
 *
 * Used at `/pair/init` and post-pair self-rename. Pure — no DOM, no globals.
 */

import { deriveDeviceParts } from "./deviceName";

const LINE_1_BUDGET = 22;

/**
 * @internal Pipeline detail. Trims a name to the line-1 character budget
 * without ellipsis. Always trim-ends so callers never emit a trailing
 * space ("Galaxy S22 Ultra Plus  "). Re-exported from the file (not the
 * package root) so the in-package test can import it.
 */
export function capAt22(name: string): string {
  if (name.length <= LINE_1_BUDGET) return name.trimEnd();
  return name.slice(0, LINE_1_BUDGET).trimEnd();
}

/** Subset of UA-CH high-entropy values relevant to naming. */
export interface UaClientHints {
  /** Hardware model — populates on Android Chrome, usually "" on desktop. */
  model: string;
}

export interface NameInput {
  /** `navigator.userAgent`. */
  ua: string;
  /** Result of `navigator.userAgentData.getHighEntropyValues(['model'])`. */
  uaCh?: UaClientHints;
  /** Names of devices already paired by this user. */
  existingPeerNames: readonly string[];
  /**
   * Surface-specific Tier C fallback (e.g. "Web Browser" /
   * "Browser Extension"). Defaults to "Browser" when unset. Each
   * surface passes the string that was historically used by the
   * legacy `deriveWebDeviceName()` / `deriveExtensionDeviceName()`
   * wrappers so the wire-format `deviceName` stays consistent
   * across versions.
   */
  tierCFallback?: string;
  /**
   * Localized Tier-B joining template ("Chrome on macOS" → de
   * "Chrome auf macOS", ja "macOS の Chrome"). Receives the UA-parsed
   * browser + OS brand names (never localized themselves) and returns
   * the composed device name. Defaults to the English
   * `"<browser> on <os>"` template when unset, preserving the
   * historical wire format. The name is generated in THIS surface's
   * locale and then persisted server-side, so the phone shows it
   * verbatim — the two surfaces belong to the same human, so their
   * locales agree in practice.
   */
  joinName?: (browser: string, os: string) => string;
}

export interface NameResult {
  name: string;
  collision: boolean;
  tier: "A" | "B" | "C";
}

/**
 * Three-tier device-naming pipeline. Pure — no DOM, no `chrome.*`.
 * The caller passes in UA + UA-CH + existing peer names; the function
 * returns the suggested name and a collision flag the pair flow uses
 * to decide whether to show the disambiguation step.
 */
export function autoNameDevice(input: NameInput): NameResult {
  const tierA = pickTierA(input.uaCh);
  if (tierA) {
    return finalize(tierA, "A", input.existingPeerNames);
  }

  const tierB = pickTierB(input.ua, input.joinName);
  if (tierB) {
    return finalize(capAt22(tierB), "B", input.existingPeerNames);
  }

  // `||` (not `??`) so an empty `tierCFallback: ""` also falls back to
  // "Browser" — an empty deviceName would land as a blank label in the
  // phone's pairing prompt.
  return finalize(input.tierCFallback || "Browser", "C", input.existingPeerNames);
}

function pickTierA(uaCh: UaClientHints | undefined): string | null {
  if (!uaCh) return null;
  const model = uaCh.model.trim();
  if (!model) return null;
  return model;
}

function pickTierB(ua: string, joinName?: (browser: string, os: string) => string): string | null {
  const { browser, os } = deriveDeviceParts(ua);
  // Tier B requires both components. A missing OS (bare "Browser"
  // fallback or a bare browser name like "Chrome") falls through to
  // Tier C so each surface can emit its own context-appropriate
  // string. Structural check on the parts — NOT a substring probe on
  // the joined name, which would break the moment the joiner is
  // localized.
  if (!os) return null;
  return joinName ? joinName(browser, os) : `${browser} on ${os}`;
}

function finalize(
  name: string,
  tier: "A" | "B" | "C",
  existingPeerNames: readonly string[]
): NameResult {
  return {
    name,
    tier,
    // Case-sensitive: both Tier A (UA-CH `model`) and Tier B
    // (`deriveDeviceName`) emit stable casing. A lowercase compare
    // would conflate "Chrome on macOS" with a user-renamed "chrome
    // on macos" and falsely flag a collision.
    collision: existingPeerNames.includes(name),
  };
}
